// AI Coach API client (Phase-1 slice). Holds the active session so the app can
// emit trustworthy UI events (builder_opened, widget_added) with the server nonce
// + strictly increasing sequence. No "completed" flag is ever sent — the server
// decides completion.
import { autonomousApi } from './autonomousApi';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/coach`;

function csrf() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

async function call(path, method = 'GET', body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `coach ${res.status}`);
  return res.json();
}

// Module-level active session (so page components can emit events without prop drilling).
let active = null; // { session_id, nonce, seq }
const listeners = new Set();
const eventListeners = new Set();
export function onCoachChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
// Subscribe to server DECISIONS on each event (deviation/recovery/branch/advance).
export function onCoachEvent(fn) { eventListeners.add(fn); return () => eventListeners.delete(fn); }
function notify() { listeners.forEach((f) => f(active)); }
function notifyEvent(d) { eventListeners.forEach((f) => f(d)); }

export const coachApi = {
  getActive: () => active,
  config: () => call('/config'),
  listWalkthroughs: (route) => call(`/walkthroughs?route=${encodeURIComponent(route || '')}`),
  // Gate-3: natural-language intent → ranked candidates + clarification (server/model never starts a session).
  intent: (text, route) => call('/intent', 'POST', { text, route: route || '' }),
  async start(walkthroughId, mode) {
    const r = await call('/session', 'POST', { walkthrough_id: walkthroughId, mode: mode || 'novice' });
    active = { session_id: r.session_id, nonce: r.nonce, seq: r.seq };
    notify();
    return r;
  },
  step: (id) => call(`/session/${id}/step`),
  control: (id, action) => call(`/session/${id}/control`, 'POST', { action }),
  // Gate-3: resume revalidates the pinned version/expiry/step before re-activating.
  resume: (id, mode) => call(`/session/${id}/resume`, 'POST', mode ? { mode } : {}),
  // Gate-3: bounded explanation (novice/expert; AI phrasing when confident, else stored text).
  explain: (id) => call(`/session/${id}/explain`),
  assist: (id) => call(`/session/${id}/assist`, 'POST', {}),
  async event(type, semanticTargetId, state) {
    if (!active) return null;
    active.seq += 1;
    const r = await call(`/session/${active.session_id}/event`, 'POST', {
      type, nonce: active.nonce, seq: active.seq, semantic_target_id: semanticTargetId, state,
    });
    notifyEvent(r);
    if (r.completed || r.status === 'completed') { active = null; notify(); }
    return r;
  },
  end() { active = null; notify(); },
};

// Convenience used by app pages to report a trustworthy UI event. Forwards to
// BOTH the walkthrough session and (Phase 4) the active autonomous plan session.
export function emitCoachEvent(type, semanticTargetId, state) {
  if (coachApi.getActive()) coachApi.event(type, semanticTargetId, state).catch(() => {});
  if (autonomousApi.getActive()) autonomousApi.forwardEvent(type, semanticTargetId, state).catch(() => {});
}
